import React, { memo, useEffect, useMemo, useRef } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from 'primereact/button'
import { Message } from 'primereact/message'
import { Controller, Resolver, useForm, useWatch } from 'react-hook-form'

import { useLocalizationContext } from '../../../../../common/components/localization-provider'
import useText from '../../../../../common/hooks/useText'
import {
    PaymentAdministrativeFeeDisplaySummary,
    PaymentDiscountCodeValidationLineRequest,
} from '../../../../../common/redux/api/web-api'
import {
    convertPriceStringToNumber,
    formatToCurrencyString,
    isEuMember,
    SWEDISH_VAT,
} from '../../../../../common/services/utilsService'
import { Dropdown } from '../../../../../common/uikit/Dropdown'
import { Input } from '../../../../../common/uikit/Input'
import { getNamesWithFallback } from '../../../../../i18n/countries'

import {
    createInvoiceDetailsSchema,
    INVOICE_DETAILS_DEFAULT_VALUES,
    type InvoicePaymentDetails,
} from './invoice-details-form-model'
import { useInvoiceDiscountCode } from './use-invoice-discount-code'

export type InvoiceDetailsFormProps = {
    onSubmit: (data: InvoicePaymentDetails) => void | Promise<void>
    isCreatingSubscription?: boolean
    administrativeFee?: PaymentAdministrativeFeeDisplaySummary | null
    currency: string
    isDiscountCodeEnabled?: boolean
    discountCodeInfo?: PaymentDiscountCodeValidationLineRequest
}

export const InvoiceDetailsForm = memo(function InvoiceDetailsForm({
    onSubmit,
    isCreatingSubscription,
    administrativeFee,
    currency,
    isDiscountCodeEnabled = false,
    discountCodeInfo,
}: InvoiceDetailsFormProps): React.ReactElement {
    const text = useText()
    const { locale } = useLocalizationContext()
    const discountCodeInputRef = useRef<HTMLInputElement>(null)
    const schema = useMemo(() => createInvoiceDetailsSchema(text), [text])

    const {
        handleSubmit,
        register,
        resetField,
        control,
        clearErrors,
        formState: { errors, isSubmitting },
    } = useForm<InvoicePaymentDetails>({
        defaultValues: INVOICE_DETAILS_DEFAULT_VALUES,
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        shouldUnregister: true,
        resolver: yupResolver(schema) as unknown as Resolver<InvoicePaymentDetails>,
    })

    const country = useWatch({
        control,
        name: 'address.country',
        defaultValue: INVOICE_DETAILS_DEFAULT_VALUES.address?.country ?? 'SE',
    })
    const discountCode = useInvoiceDiscountCode({
        enabled: isDiscountCodeEnabled,
        discountCodeInfo,
        text,
    })

    useEffect(() => {
        if (country === 'SE') {
            resetField('vatNumber', { defaultValue: undefined })
            clearErrors('vatNumber')
            return
        }

        if (country && isEuMember(country)) {
            resetField('organizationNumber', { defaultValue: undefined })
            clearErrors('organizationNumber')
            return
        }

        resetField('vatNumber', { defaultValue: undefined })
        resetField('organizationNumber', { defaultValue: undefined })
        clearErrors(['vatNumber', 'organizationNumber'])
    }, [clearErrors, country, resetField])

    const countryOptions = useMemo(
        () =>
            Object.entries(getNamesWithFallback(locale))
                .map(([value, label]) => ({ label, value }))
                .sort((first, second) => first.label.localeCompare(second.label, locale)),
        [locale]
    )

    const administrativeFeeWithVat = formatToCurrencyString(
        administrativeFee?.currency ?? currency,
        convertPriceStringToNumber(administrativeFee?.priceInCurrency ?? '0') * SWEDISH_VAT,
        locale
    )

    const submitInvoiceDetails = handleSubmit((formData) => {
        const discountCodeSubmission = discountCode.validateForSubmission()
        if (!discountCodeSubmission.isValid) {
            discountCodeInputRef.current?.focus()
            return
        }

        return onSubmit({
            ...formData,
            discountCode: discountCodeSubmission.discountCode,
        })
    })

    return (
        <form id="invoicing-form" className="flex flex-column gap-3" noValidate onSubmit={submitInvoiceDetails}>
            <div className="flex flex-column gap-2">
                <Input
                    id="companyName"
                    placeholder={text('lblOrganisationName')}
                    error={errors.companyName?.message}
                    {...register('companyName')}
                />
                <Input
                    id="address-line1"
                    className="mt-3"
                    placeholder={text('lblAddressLineOne')}
                    error={errors.address?.line1?.message}
                    {...register('address.line1')}
                />
                <Input
                    id="address-line2"
                    placeholder={`${text('lblAddressLineTwo')} (${text('lblOptional').toLowerCase()})`}
                    error={errors.address?.line2?.message}
                    {...register('address.line2')}
                />
                <div className="flex gap-2">
                    <Input
                        id="postal-code"
                        placeholder={text('lblPostalCode')}
                        error={errors.address?.postalCode?.message}
                        {...register('address.postalCode')}
                        className="w-4"
                    />
                    <Input
                        id="city"
                        placeholder={text('lblCity')}
                        error={errors.address?.city?.message}
                        {...register('address.city')}
                        className="w-8"
                    />
                </div>
                <Input
                    id="state"
                    placeholder={`${text('lblState')} (${text('lblOptional').toLowerCase()})`}
                    error={errors.address?.state?.message}
                    {...register('address.state')}
                />
                <Controller
                    name="address.country"
                    control={control}
                    render={({ field }) => (
                        <Dropdown
                            value={field.value || null}
                            options={countryOptions}
                            placeholder={text('lblSelectCountry')}
                            showClear={false}
                            filter
                            optionLabel="label"
                            filterBy="label,value"
                            id="country"
                            className="w-full"
                            invalid={!!errors.address?.country}
                            onChange={(event) => field.onChange(event.value ?? undefined)}
                            onBlur={field.onBlur}
                        />
                    )}
                />
                {country === 'SE' ? (
                    <Input
                        id="organisations-number"
                        className="mt-3"
                        placeholder={text('lblCompanyRegistrationNumber')}
                        error={errors.organizationNumber?.message}
                        {...register('organizationNumber')}
                    />
                ) : (
                    <Input
                        id="vat-number"
                        className="mt-3"
                        placeholder={text('lblVatNumber')}
                        error={errors.vatNumber?.message}
                        disabled={!!country && !isEuMember(country)}
                        {...register('vatNumber')}
                    />
                )}
                <Input
                    id="yourReference"
                    placeholder={`${text('lblInvoiceYourReference')} (${text('lblOptional').toLowerCase()})`}
                    error={errors.yourReference?.message}
                    {...register('yourReference')}
                />
                <Input
                    id="email"
                    placeholder={text('lblBillingEmail')}
                    error={errors.email?.message}
                    {...register('email')}
                />
                {isDiscountCodeEnabled ? (
                    <div data-testid="discount-code-section" className="flex flex-column gap-2 w-full mt-3">
                        <div className="flex gap-2 align-items-start">
                            <div className="flex-1">
                                <Input
                                    ref={discountCodeInputRef}
                                    id="discount-code"
                                    name="discountCode"
                                    placeholder={text('lblInvoiceDiscountCodePlaceholder')}
                                    value={discountCode.input}
                                    error={discountCode.submissionError ?? undefined}
                                    onChange={(event) => discountCode.setInput(event.target.value)}
                                    disabled={discountCode.isInputDisabled}
                                />
                            </div>
                            <Button
                                type="button"
                                label={text('btnApply')}
                                onClick={() => void discountCode.apply()}
                                loading={discountCode.isApplying}
                                disabled={discountCode.isApplyDisabled}
                            />
                        </div>
                        {discountCodeInfo?.quantity === 0 ? (
                            <small className="block mt-1 px-1 text-xs text-500 line-height-2">
                                {text('lblInvoiceDiscountCodeDisabledZeroConnectors')}
                            </small>
                        ) : null}
                        {discountCode.feedback ? (
                            <Message severity={discountCode.feedback.severity} text={discountCode.feedback.text} />
                        ) : null}
                    </div>
                ) : null}
            </div>

            <div>
                <p className="text-sm text-gray-700 mb-0">
                    {text('lblInvoiceAdministrationFee', { adminFee: administrativeFeeWithVat })}
                </p>
                <p className="text-sm text-gray-700">{text('lblInvoiceAdministrationFeeBody')}</p>
            </div>

            <Button
                label={text('lblConfirmAndActivateSubscription')}
                type="submit"
                disabled={isSubmitting || !!isCreatingSubscription}
                className="w-full"
            />
        </form>
    )
})
